import { Capacitor } from '@capacitor/core';

/**
 * Platform detection utility for identifying the current runtime environment
 */

export type Platform = 'android' | 'chrome' | 'unsupported';
export type AppStorePlatform = 'ios' | 'android' | 'other';

/**
 * Detects if the app is running on Android via Capacitor
 */
export const isAndroidCapacitor = (): boolean => {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
};

/**
 * Detects if the app is running in any Capacitor native platform
 */
export const isCapacitorPlatform = (): boolean => {
  return Capacitor.isNativePlatform();
};

/**
 * Detects if the device is iOS, iPadOS, or macOS
 */
export const isApplePlatform = (): boolean => {
  const userAgent = navigator.userAgent;
  const platform = navigator.platform;
  
  // First check if it's Android or Windows in the user agent (to handle Puppeteer/testing)
  const lowerUserAgent = userAgent.toLowerCase();
  if (lowerUserAgent.includes('android') || lowerUserAgent.includes('windows')) return false;
  
  // Check for iOS and iPadOS
  if (/iPhone|iPad|iPod/.test(userAgent)) return true;
  
  // Check for Mac (including Apple Silicon Macs) - but only if not Windows UA
  if (platform.startsWith('Mac') && !lowerUserAgent.includes('windows')) return true;
  
  // iPad on iOS 13+ reports as Mac in desktop mode
  if (platform === 'MacIntel' && navigator.maxTouchPoints > 1) return true;
  
  return false;
};

/**
 * Detects if the device is Android (not via Capacitor)
 */
export const isAndroidWeb = (): boolean => {
  if (isAndroidCapacitor()) return false;
  const userAgent = navigator.userAgent.toLowerCase();
  return userAgent.includes('android');
};

/**
 * Gets the platform type for app store redirect
 */
export const getAppStorePlatform = (): AppStorePlatform => {
  if (isApplePlatform()) return 'ios';
  if (isAndroidWeb()) return 'android';
  return 'other';
};

/**
 * Detects if the app is running in Chrome browser (not on Android)
 */
export const isChromeBrowser = (): boolean => {
  if (isAndroidCapacitor()) return false;
  
  const userAgent = navigator.userAgent.toLowerCase();
  const isChrome = userAgent.includes('chrome') && !userAgent.includes('edg');
  
  return isChrome;
};

/**
 * Checks if the current platform is supported
 * Supported platforms: Android 10+ via Capacitor, Chrome browser
 */
export const isPlatformSupported = async (): Promise<boolean> => {
  if (isAndroidCapacitor()) {
    // For Android, we need to check the version
    try {
      const { Device } = await import('@capacitor/device');
      const info = await Device.getInfo();
      
      // Android API level 29 corresponds to Android 10
      const androidVersion = parseInt(info.osVersion || '0');
      return androidVersion >= 10;
    } catch (error) {
      console.error('Error checking Android version:', error);
      return false;
    }
  }
  
  return isChromeBrowser();
};

/**
 * Gets the current platform type
 */
export const getPlatform = async (): Promise<Platform> => {
  if (isAndroidCapacitor()) {
    const supported = await isPlatformSupported();
    return supported ? 'android' : 'unsupported';
  }
  
  if (isChromeBrowser()) {
    return 'chrome';
  }
  
  return 'unsupported';
};

/**
 * Gets a user-friendly platform name for display
 */
export const getPlatformDisplayName = async (): Promise<string> => {
  const platform = await getPlatform();
  
  switch (platform) {
    case 'android':
      return 'Android';
    case 'chrome':
      return 'Chrome';
    default:
      return 'Unsupported Browser';
  }
};